import * as React from "react";
import { useEffect } from "react";

// Third Party
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// UI Components
import {
	Card,
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
} from "@/components/ui/card";
import { ArrowLeft, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { FileUpload } from "@/components/FileUpload";
import { Spinner } from "@/components/ui/spinner";
import { useToast } from "@/components/ui/use-toast";
import DinamixSkeletonTable from "@/components/skeleton/DinamixTableSkeleton"; // Services and Utils
import { getDivDetails, editDividends } from "@/services/tax-obligations";

export const Route = createFileRoute(
	"/_layout-private/report/tax-obligations/dividend-plan/edit",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const divId = localStorage.getItem("divId")?.replace(/^"(.+)"$/, "$1");

	const [formData, setFormData] = React.useState({
		plannedDividend: "",
		actualDividend: "",
		totalPhase: "",
		phase: "",
		docFile: "",
	});

	// Fetch dividend details
	const { data: divDetails, isLoading } = useQuery({
		queryKey: ["dividend-details", divId],
		queryFn: () => getDivDetails(divId as string),
		enabled: !!divId,
	});

	// Update form data when details are loaded
	useEffect(() => {
		if (divDetails?.data) {
			setFormData({
				plannedDividend: formatNumberWithCommas(
					divDetails?.data?.plannedDividend || 0,
				),
				actualDividend: formatNumberWithCommas(
					divDetails?.data?.actualDividend || 0,
				),
				totalPhase: String(divDetails?.data?.totalPhase || 0),
				phase: String(divDetails?.data?.phase || 0),
				docFile: divDetails?.data?.docFile || "",
			});
		}
	}, [divDetails]);

	// Mutation for editing dividend
	const mutation = useMutation({
		mutationFn: (data: any) => editDividends(divId as string, data),
		onSuccess: () => {
			toast({
				title: "ເພີ່ມຂໍ້ມູນສຳເລັດ",
				description: "ແກ້ໄຂແຜນການຈ່າຍເງິນປັນຜົນສຳເລັດ",
				variant: "success",
			});
			queryClient.invalidateQueries({ queryKey: ["dividend-details", divId] });
			navigate({ to: "/report/tax-obligations?active=dividendPlan" });
		},
		onError: (error: any) => {
			toast({
				title: "ເກີດຂໍ້ຜິດພາດ",
				description: "ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
				variant: "destructive",
			});
		},
	});

	function formatNumberWithCommas(value: string | number) {
		return new Intl.NumberFormat().format(Number(value) || 0);
	}

	function parseFormattedNumber(value: string) {
		return Number(value.replace(/,/g, "")) || 0;
	}

	const handleInputChange = (name: string, value: string) => {
		if (name === "totalPhase" || name === "phase") {
			setFormData((prev) => ({ ...prev, [name]: value }));
			return;
		}

		const processedValue = value.replace(/,/g, "");
		if (!/^\d*$/.test(processedValue)) return;

		setFormData((prev) => ({
			...prev,
			[name]: formatNumberWithCommas(processedValue),
		}));
	};

	const handleFileUpload = (file: string) => {
		setFormData((prev) => ({ ...prev, docFile: file }));
	};

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (!formData.docFile) {
			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ກະລຸນາອັບໂຫຼດເອກະສານກ່ອນບັນທຶກ",
				variant: "destructive",
			});

			return;
		}

		const submitData = {
			plannedDividend: parseFormattedNumber(formData?.plannedDividend),
			actualDividend: parseFormattedNumber(formData?.actualDividend),
			totalPhase: Number(formData?.totalPhase),
			phase: Number(formData?.phase),
			docFile: formData?.docFile,
		};
		mutation.mutate(submitData);
	};

	//  if (!divDetails?.data) return <div>No data found</div>;

	return (
		<section>
			<Breadcrumb>
				<BreadcrumbList>
					<BreadcrumbItem>
						<BreadcrumbLink>
							<Link replace to="/report/tax-obligations?active=dividendPlan">
								ລາຍງານການເງິນຂອງບໍລິສັດ
							</Link>
						</BreadcrumbLink>
					</BreadcrumbItem>
					<BreadcrumbSeparator />
					<BreadcrumbItem>
						<BreadcrumbLink>
							<Link replace to="/report/tax-obligations/dividend-plan">
								ລາຍງານການເງິນຂອງບໍລິສັດ
							</Link>
						</BreadcrumbLink>
					</BreadcrumbItem>
					<BreadcrumbSeparator />
					<BreadcrumbItem>
						<BreadcrumbPage>ແກ້ໄຂລາຍງານພັນທະອາກອນ</BreadcrumbPage>
					</BreadcrumbItem>
				</BreadcrumbList>
			</Breadcrumb>

			<Card className="mt-8">
				<CardHeader>
					<button
						type="button"
						onClick={() =>
							navigate({ to: "/report/tax-obligations?active=dividendPlan" })
						}
						className="flex gap-1 items-center mb-4"
					>
						<ArrowLeft className="w-5 h-5" />
						ກັບຄືນ
					</button>
					<CardTitle>
						{divDetails?.data
							? `ລາຍລະອຽດແຜນການຈ່າຍເງິນປັນຜົນ ປີ ${divDetails.data.yearly}`
							: "ບໍ່ມີຂໍມູນ"}
					</CardTitle>
					<CardDescription>ລາຍລະອຽດ</CardDescription>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<DinamixSkeletonTable rows={4} columns={1} />
					) : (
						<form
							onSubmit={handleSubmit}
							className="flex flex-col items-center space-y-4"
						>
							<div className="grid gap-4">
								<div>
									<Label>ແຜນເງິນປັນຜົນ</Label>
									<div className="flex gap-2 items-center">
										<Input
											type="text"
											value={formData?.plannedDividend}
											onChange={(e) =>
												handleInputChange("plannedDividend", e.target.value)
											}
											className="w-[350px] text-right"
											inputMode="numeric"
										/>
										<span>ກີບ</span>
									</div>
								</div>

								<div>
									<Label>ຈຳນວນງວດທັງໝົດ</Label>
									<div className="flex gap-2 items-center">
										<Input
											type="text"
											value={formData?.totalPhase}
											onChange={(e) =>
												handleInputChange("totalPhase", e.target.value)
											}
											className="w-[350px] text-right"
											inputMode="numeric"
										/>
										<span>ງວດ</span>
									</div>
								</div>
							</div>

							<div className="my-4 w-[300px]">
								<FileUpload
									currentFile={formData?.docFile}
									onFileUpload={handleFileUpload}
								/>
							</div>

							<div className="flex gap-2 justify-end items-center w-full">
								<Button
									type="button"
									variant="destructive"
									onClick={() =>
										navigate({
											to: "/report/tax-obligations?active=dividendPlan",
										})
									}
								>
									ຍົກເລີກ
								</Button>
								<Button
									disabled={mutation?.isPending}
									type="submit"
									className="flex gap-2 items-center"
								>
									{mutation?.isPending ? (
										<Spinner className="text-white" />
									) : (
										<>
											<Save className="mr-2" /> ບັນທຶກ
										</>
									)}
								</Button>
							</div>
						</form>
					)}
				</CardContent>
			</Card>
		</section>
	);
}
